class SFCommunityCarousel extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.grid = this.querySelector('[data-feed-grid]');
    this.status = this.querySelector('[data-feed-status]');
    this.dialog = this.querySelector('dialog');
    this.media = this.querySelector('[data-viewer-media]');
    this.caption = this.querySelector('[data-viewer-caption]');
    this.link = this.querySelector('[data-viewer-link]');
    this.closeButton = this.querySelector('[data-viewer-close]');
    if (!this.grid || !this.dialog) return;
    this.fallback = [...this.grid.children].map((card) => ({
      id: card.dataset.mediaId,
      type: card.dataset.video ? 'video' : 'image',
      image: this.safeURL(card.dataset.image),
      video: this.safeURL(card.dataset.video),
      permalink: this.instagramURL(card.dataset.permalink),
      alt: card.dataset.alt || this.dataset.imageLabel,
      caption: card.dataset.caption || '',
    })).filter((item) => item.id && item.image);
    this.items = this.fallback;
    this.grid.querySelectorAll('img').forEach((image) => this.prepareThumbnail(image));
    [...this.grid.querySelectorAll('button')].forEach((button, index) => {
      button.dataset.index = String(index);
      button.disabled = false;
    });
    this.grid.addEventListener('click', (event) => {
      const button = event.target.closest('[data-index]');
      if (button && this.grid.contains(button)) this.openItem(Number(button.dataset.index), button);
    }, { signal });
    this.grid.addEventListener('error', (event) => {
      if (!(event.target instanceof HTMLImageElement)) return;
      const card = event.target.closest('.sf-instagram-card');
      if (!card) return;
      event.target.hidden = true;
      card.classList.add('is-unavailable');
      const button = card.querySelector('button');
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-label', this.dataset.unavailableLabel);
      }
      const note = card.querySelector('[data-card-error]');
      if (note) { note.hidden = false; note.textContent = this.dataset.unavailableLabel; }
    }, { signal, capture: true });
    this.closeButton.addEventListener('click', () => this.dialog.close(), { signal });
    this.dialog.addEventListener('close', () => this.closeItem(), { signal });
    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const bounds = this.dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
    }, { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.media.querySelector('video')?.pause();
    }, { signal });
    window.addEventListener('pagehide', () => this.media.querySelector('video')?.pause(), { signal });
    const move = (direction) => this.grid.scrollBy({ left: direction * this.grid.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    const previousButton = this.querySelector('[data-feed-previous]');
    const nextButton = this.querySelector('[data-feed-next]');
    previousButton?.addEventListener('click', () => move(-1), { signal });
    nextButton?.addEventListener('click', () => move(1), { signal });
    this.setState(this.fallback.length ? 'manual' : 'empty');
    const endpoint = this.safeURL(this.dataset.endpoint);
    if (!endpoint) return;
    const parsed = new URL(endpoint);
    if (parsed.hostname !== 'api.nfcube.com' || parsed.pathname !== '/feed/v6') return;
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.fetchFeed(endpoint);
      }, { rootMargin: '200px' });
      this.observer.observe(this);
    } else this.fetchFeed(endpoint);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.request?.abort();
    if (this.dialog?.open) this.dialog.close();
    this.closeItem();
    this.controller?.abort();
  }

  prepareThumbnail(image) {
    const updateCrop = () => image.classList.toggle('sf-thumbnail-landscape', image.naturalWidth > image.naturalHeight * 1.35);
    if (image.complete && image.naturalWidth) updateCrop();
    else image.addEventListener('load', updateCrop, { once: true, signal: this.controller.signal });
  }

  safeURL(value) {
    if (!value) return '';
    try {
      const url = new URL(value, location.origin);
      if (url.protocol !== 'https:' || url.username || url.password) return '';
      return url.href;
    } catch { return ''; }
  }

  instagramURL(value) {
    const safe = this.safeURL(value);
    if (!safe) return '';
    const url = new URL(safe);
    return ['instagram.com', 'www.instagram.com'].includes(url.hostname) ? safe : '';
  }

  normalise(items) {
    if (!Array.isArray(items)) throw new Error('Invalid items');
    const seen = new Set();
    return items.slice(0, 50).map((item) => {
      if (!item || typeof item !== 'object') return null;
      const timestamp = Date.parse(item.published_at);
      const image = this.safeURL(item.image_url);
      const video = item.type === 'video' ? this.safeURL(item.video_url) : '';
      if (!item.id || !image || !Number.isFinite(timestamp) || seen.has(String(item.id))) return null;
      seen.add(String(item.id));
      return {
        id: String(item.id), timestamp, image, video,
        type: video ? 'video' : 'image',
        permalink: this.instagramURL(item.permalink),
        alt: String(item.alt || item.caption || this.dataset.imageLabel).slice(0, 240),
        caption: String(item.caption || '').slice(0, 2200),
      };
    }).filter(Boolean).sort((a, b) => b.timestamp - a.timestamp).slice(0, 6);
  }

  async fetchFeed(endpoint) {
    this.setState('loading');
    this.grid.setAttribute('aria-busy', 'true');
    this.request = new AbortController();
    const timer = setTimeout(() => this.request.abort(), 7000);
    try {
      const response = await fetch(endpoint, {
        signal: this.request.signal,
        credentials: 'omit',
        headers: { Accept: 'application/json' },
        referrerPolicy: 'no-referrer',
      });
      if (!response.ok) throw new Error('Feed unavailable');
      const data = await response.json();
      if (data.meta?.code !== 200 || !Array.isArray(data.data)) throw new Error('Invalid feed');
      data.items = data.data.map((post) => ({ id: post.id, published_at: new Date(Number(post.created_time) * 1000).toISOString(), image_url: post.images?.standard_resolution?.url, type: post.type, video_url: post.videos?.standard_resolution?.url, permalink: post.link, alt: post.alt_text || post.caption?.text?.slice(0, 160), caption: post.caption?.text }));
      data.status = 'live';
      const items = this.normalise(data.items);
      if (data.status === 'empty') {
        this.render([]);
        this.setState('empty');
      } else if (items.length) {
        this.render(items);
        this.setState(data.status);
      } else throw new Error('No usable media');
    } catch {
      if (this.isConnected) this.setState(this.fallback.length ? 'manual' : 'error');
    } finally {
      clearTimeout(timer);
      this.grid?.setAttribute('aria-busy', 'false');
    }
  }

  setState(state) {
    this.dataset.state = state;
    if (!this.status) return;
    this.status.textContent = this.dataset[`${state}Label`] || '';
  }

  render(items) {
    if (this.dialog.open) this.dialog.close();
    this.items = items;
    const fragment = document.createDocumentFragment();
    for (let index = 0; index < Math.min(items.length, 6); index += 1) {
      const card = document.createElement('div');
      card.className = 'sf-instagram-card';
      const item = items[index];
      if (!item) {
        card.classList.add('sf-instagram-placeholder');
        card.setAttribute('aria-hidden', 'true');
        fragment.append(card);
        continue;
      }
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sf-instagram-open';
      button.dataset.index = String(index);
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-label', `${item.video ? this.dataset.playLabel : this.dataset.viewLabel}: ${item.alt}`);
      const img = new Image();
      img.width = 600;
      img.height = 600;
      img.alt = item.alt;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.referrerPolicy = 'no-referrer';
      img.src = item.image;
      this.prepareThumbnail(img);
      button.append(img);
      if (item.video) {
        const play = document.createElement('span');
        play.className = 'sf-instagram-play';
        play.setAttribute('aria-hidden', 'true');
        play.textContent = '▶';
        button.append(play);
      }
      const error = document.createElement('span');
      error.dataset.cardError = '';
      error.className = 'sf-instagram-card-error';
      error.hidden = true;
      button.append(error);
      card.append(button);
      fragment.append(card);
    }
    this.grid.replaceChildren(fragment);
  }

  openItem(index, opener) {
    const item = this.items[index];
    if (!item || typeof this.dialog.showModal !== 'function') return;
    this.closeItem();
    this.opener = opener;
    this.caption.textContent = item.caption || item.alt;
    this.link.hidden = !item.permalink;
    if (item.permalink) this.link.href = item.permalink;
    else this.link.removeAttribute('href');
    let element;
    if (item.video) {
      element = document.createElement('video');
      element.controls = true;
      element.playsInline = true;
      element.preload = 'none';
      element.poster = item.image;
      element.setAttribute('aria-label', item.alt);
    } else {
      element = new Image();
      element.alt = item.alt;
    }
    element.addEventListener('error', () => {
      element.pause?.();
      const message = document.createElement('p');
      message.textContent = this.dataset.unavailableLabel;
      this.media.replaceChildren(message);
    }, { once: true });
    this.media.replaceChildren(element);
    this.previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.dialog.showModal();
    this.closeButton.focus({ preventScroll: true });
    element.src = item.video || item.image;
    if (item.video) element.play().catch(() => {});
  }

  closeItem() {
    const video = this.media?.querySelector('video');
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
    this.media?.replaceChildren();
    if (this.previousOverflow !== undefined) {
      document.documentElement.style.overflow = this.previousOverflow;
      this.previousOverflow = undefined;
    }
    if (this.opener?.isConnected && !this.opener.disabled) this.opener.focus({ preventScroll: true });
    this.opener = null;
  }
}

if (!customElements.get('sf-community-carousel')) customElements.define('sf-community-carousel', SFCommunityCarousel);

